import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { validateExternalOrder } from '../../api/externalOrders';
import { useShop } from '../ShopState';

export function Gate() {
  const navigate = useNavigate();
  const { setVerification } = useShop();
  const [firstName, setFirstName] = useState('');
  const [externalOrderId, setExternalOrderId] = useState('');
  const [state, setState] = useState<'idle' | 'notfound' | 'failed' | 'loading'>('idle');
  const [exhausted, setExhausted] = useState<{ allowance: number } | null>(null);
  const [shippingClosed, setShippingClosed] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState('loading');
    setExhausted(null);
    setShippingClosed(false);
    // A thrown request is not a verdict on the order. Reporting both as "not found" sends the
    // holder chasing a typo while the real fault is server-side.
    try {
      const res = await validateExternalOrder(firstName, externalOrderId);
      if (res.valid) {
        // Out of shirts is a valid order, not an unknown one, and it gets its own message. The
        // holder is not sent to any existing order: the gate deliberately never learns which
        // orders exist, so recovery goes through the emailed link or the lookup form.
        if (res.remaining <= 0) {
          setExhausted({ allowance: res.allowance });
          setState('idle');
          return;
        }
        // Every earlier parcel has already gone out, so there is nothing left to add shirts to.
        // Stopping here rather than at checkout spares the holder filling a cart the shop will
        // refuse, and refusing at all is deliberate: the alternative is charging for a second
        // parcel they were told they would not pay for.
        if (res.shippingMode === 'closed') {
          setShippingClosed(true);
          setState('idle');
          return;
        }
        // The typed first name is kept deliberately: holders may be seeded ASCII-folded, and this
        // name goes on the parcel label. The id is taken from the API in its stored form.
        setVerification({
          firstName,
          externalOrderId: res.externalOrderId,
          allowance: res.allowance,
          alreadyOrdered: res.alreadyOrdered,
          remaining: res.remaining,
          byColor: res.byColor,
          shippingMode: res.shippingMode,
        });
        navigate('/sklep');
        return;
      }
      setState('notfound');
    } catch {
      setState('failed');
    }
  };

  return (
    <div style={{ width: '100%', minHeight: '100vh', display: 'flex' }}>
      {/* LEFT — poster art */}
      <div
        style={{
          flex: '0 0 58%',
          position: 'relative',
          borderRight: '2px solid var(--shop-fg)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 24,
            left: 28,
            right: 28,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          className="mono"
        >
          <div style={{ fontSize: 11, letterSpacing: '.22em', color: 'var(--orange)' }}>
            OPERACJA TENERYFA · INICJATYWA FANOWSKA
          </div>
          <div style={{ fontSize: 11, letterSpacing: '.18em', color: 'rgba(var(--shop-muted-rgb),.6)' }}>
            SKLEP / EDYCJA 2026
          </div>
        </div>

        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 28px 60px',
            gap: 24,
          }}
        >
          <div style={{ width: 300, height: 300, flex: '0 0 auto' }}>
            <img
              src="/logo.png"
              alt=""
              style={{ width: '100%', display: 'block', filter: 'drop-shadow(0 30px 60px rgba(0,0,0,.45))' }}
            />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="display" style={{ fontSize: 64, lineHeight: 0.92, color: 'var(--shop-fg)' }}>
              KOSZULKA FANOWSKA
            </div>
            <div
              className="mono"
              style={{
                marginTop: 12,
                fontSize: 12,
                letterSpacing: '.18em',
                color: 'rgba(var(--shop-muted-rgb),.65)',
              }}
            >
              28.11.2026 · TENERYFA · 100% BAWEŁNA · SITODRUK
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — gate form */}
      <form
        onSubmit={submit}
        style={{
          flex: 1,
          padding: '64px 56px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <fieldset
          disabled={state === 'loading'}
          style={{
            border: 0,
            padding: 0,
            margin: 0,
            display: 'contents',
          }}
        >
        <div
          className="mono"
          style={{ fontSize: 11, color: 'var(--orange)', letterSpacing: '.2em', marginBottom: 20 }}
        >
          ① WERYFIKACJA ZAMÓWIENIA
        </div>
        <div className="display" style={{ fontSize: 56, lineHeight: 0.95 }}>
          MASZ BILET<span style={{ color: 'var(--orange)' }}>?</span>
        </div>
        <div
          style={{
            marginTop: 16,
            fontSize: 14,
            color: 'rgba(var(--shop-muted-rgb),.75)',
            lineHeight: 1.55,
            maxWidth: 420,
          }}
        >
          Koszulki dostępne tylko dla osób, które kupiły bilet na koncert. Wpisz numer zamówienia z
          potwierdzenia zakupu biletów i imię <b>dokładnie</b> jak przy zakupie - sprawdzamy w bazie.
        </div>

        <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 420 }}>
          <div className="field">
            <label
              htmlFor="dgate-firstname"
              className="display"
              style={{ fontSize: 12, letterSpacing: '.14em', color: 'var(--shop-fg)' }}
            >
              IMIĘ (JAK PRZY ZAKUPIE BILETÓW)
            </label>
            <input
              id="dgate-firstname"
              className="input"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{ marginTop: 6 }}
            />
          </div>
          <div className="field">
            <label
              htmlFor="dgate-order"
              className="display"
              style={{ fontSize: 12, letterSpacing: '.14em', color: 'var(--shop-fg)' }}
            >
              NUMER ZAMÓWIENIA
            </label>
            <input
              id="dgate-order"
              className="input mono"
              required
              value={externalOrderId}
              onChange={(e) => setExternalOrderId(e.target.value)}
              style={{ marginTop: 6 }}
            />
            <div
              className="mono"
              style={{
                fontSize: 10,
                marginTop: 6,
                color: 'rgba(var(--shop-muted-rgb),.5)',
                letterSpacing: '.1em',
              }}
            >
              ZNAJDZIESZ GO W POTWIERDZENIU ZAKUPU BILETÓW
            </div>
          </div>
        </div>

        {exhausted && (
          <div
            style={{
              marginTop: 18,
              padding: 12,
              border: '2px solid var(--orange-deep)',
              background: 'rgba(154,52,8,.08)',
              maxWidth: 420,
            }}
          >
            <div
              className="display"
              style={{ fontSize: 13, letterSpacing: '.12em', color: 'var(--orange-deep)' }}
            >
              LIMIT WYKORZYSTANY
            </div>
            <div style={{ fontSize: 12, marginTop: 4, color: 'rgba(var(--shop-muted-rgb),.7)' }}>
              Na to zamówienie kupiono już {exhausted.allowance} z {exhausted.allowance} koszulek.{' '}
              <Link to="/zamowienie" style={{ color: 'var(--orange)' }}>
                Znajdź swoje zamówienie →
              </Link>
            </div>
          </div>
        )}

        {shippingClosed && (
          <div
            style={{
              marginTop: 18,
              padding: 12,
              border: '2px solid var(--orange-deep)',
              background: 'rgba(154,52,8,.08)',
              maxWidth: 420,
            }}
          >
            <div
              className="display"
              style={{ fontSize: 13, letterSpacing: '.12em', color: 'var(--orange-deep)' }}
            >
              PACZKA JUŻ WYSŁANA
            </div>
            <div style={{ fontSize: 12, marginTop: 4, color: 'rgba(var(--shop-muted-rgb),.7)' }}>
              Twoje koszulki z tego zamówienia są już w drodze, więc nie dołożymy do nich kolejnych.
              Napisz na{' '}
              <a href="mailto:kontakt@operacjateneryfa.pl" style={{ color: 'var(--orange)' }}>
                kontakt@operacjateneryfa.pl
              </a>
              , jeśli chcesz domówić.
            </div>
          </div>
        )}

        {state === 'notfound' && (
          <div
            style={{
              marginTop: 18,
              padding: 12,
              border: '2px solid var(--orange-deep)',
              background: 'rgba(154,52,8,.08)',
              maxWidth: 420,
            }}
          >
            <div
              className="display"
              style={{ fontSize: 13, letterSpacing: '.12em', color: 'var(--orange-deep)' }}
            >
              NIE ZNALEŹLIŚMY TEGO ZAMÓWIENIA
            </div>
            <div style={{ fontSize: 12, marginTop: 4, color: 'rgba(var(--shop-muted-rgb),.7)' }}>
              Sprawdź pisownię imienia i numer zamówienia. Nadal nie działa?{' '}
              <a href="mailto:kontakt@operacjateneryfa.pl" style={{ color: 'var(--orange)' }}>
                kontakt@operacjateneryfa.pl
              </a>
            </div>
          </div>
        )}

        {state === 'failed' && (
          <div
            style={{
              marginTop: 18,
              padding: 12,
              border: '2px solid var(--orange-deep)',
              background: 'rgba(154,52,8,.08)',
              maxWidth: 420,
            }}
          >
            <div
              className="display"
              style={{ fontSize: 13, letterSpacing: '.12em', color: 'var(--orange-deep)' }}
            >
              COŚ POSZŁO NIE TAK
            </div>
            <div style={{ fontSize: 12, marginTop: 4, color: 'rgba(var(--shop-muted-rgb),.7)' }}>
              Nie udało się sprawdzić zamówienia - to nasza wina, nie Twoich danych. Spróbuj za chwilę.
            </div>
          </div>
        )}

        <div style={{ marginTop: 28, display: 'flex', gap: 12, alignItems: 'stretch', maxWidth: 420 }}>
          <button
            type="submit"
            className="btn"
            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10, whiteSpace: 'nowrap' }}
            disabled={state === 'loading'}
            aria-busy={state === 'loading'}
          >
            {state === 'loading' && <span className="spinner" aria-hidden="true" />}
            {state === 'loading' ? 'SPRAWDZAM…' : 'DO ZAMÓWIENIA →'}
          </button>
          <a
            href="http://fast-tickets.com/events/43"
            target="_blank"
            rel="noreferrer"
            className="btn white"
            // Secondary action: sized to its label so the primary keeps the remaining width. Both
            // buttons at 18px would overflow the 420px form column.
            style={{ flex: '0 0 auto', fontSize: 14, padding: '14px 14px', textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            ↗ KUP BILET
          </a>
        </div>

        <div className="mono" style={{ marginTop: 16, fontSize: 11, letterSpacing: '.1em' }}>
          <Link to="/zamowienie" style={{ color: 'var(--orange)', textDecoration: 'none' }}>
            MASZ JUŻ ZAMÓWIENIE? ZNAJDŹ JE →
          </Link>
        </div>
        </fieldset>

        <div
          style={{
            position: 'absolute',
            bottom: 28,
            left: 56,
            right: 56,
            fontSize: 10,
            color: 'rgba(var(--shop-muted-rgb),.45)',
            letterSpacing: '.18em',
          }}
          className="mono"
        >
          <span>OPERACJA-TENERYFA.PL</span>
        </div>
      </form>
    </div>
  );
}
